import React from 'react';

// Change log entries: date-wise, each with type (New / Update / Bugfix) and items
const CHANGE_LOG_DATA = [
  {
    version: 'V1.0.3',
    date: 'Feb 17, 2026',
    sections: [
      {
        type: 'New',
        title: 'Templates & navigation',
        items: [
          'Custom email templates: add named templates (name, subject, body, header, signature) on the Templates page; they appear in the Send Emails template dropdown under a "Custom" section.',
          'Note Pad link in the sidebar: opens Excalidraw in a new tab for quick sketching/notes.',
          'Signature image: paste or choose an image file for templates; the actual image is stored (data URL), not just a URL field.',
          'Email templates table: migration added so saving templates works (run ensure-email-templates-table.sql in Supabase if you see "table email_templates not in schema cache").'
        ]
      },
      {
        type: 'Update',
        title: 'Templates & Send Emails',
        items: [
          'Removed the extra "Line spacing…" dropdown from the toolbar; the main line-height dropdown (e.g. 1.2–1.8) is the only line-spacing control.',
          'Sent emails now use the exact template HTML (no rewriting of paragraph markup); what you see in the template is what gets sent.',
          'Send Emails: each row has a Template dropdown (stage templates plus custom templates by name) so you can pick which template to use per recipient.'
        ]
      },
      {
        type: 'Update',
        title: 'Post Sales',
        items: [
          'Date filter now filters by when post-sales work was done (post_sales.updated_at), not by lead creation date; BDE filter uses the post-sales person (post_sales_bde_name).',
          'Removed the duplicate filter bar below "Post Sales (n)" (search, From/To dates, Clear); the main FilterBar at the top is the only filter row.',
          'Table header styling: white background, subtle top/bottom border, dark grey uppercase labels, and increased padding for a cleaner look.'
        ]
      },
      {
        type: 'Bugfix',
        title: 'Post Sales & Templates',
        items: [
          'Post Sales date filter logic: backend only uses post_sales.updated_at for date range; accepts both date_from/date_to and dateFrom/dateTo; client sends dates in YYYY-MM-DD.',
          'EmailTemplates: fixed JSX parse error for custom templates list (explicit return in map callback).',
          'Redeployed frontend and backend to the same production URLs (ags-sales-hub.vercel.app and backend-six-sooty-37.vercel.app) with no breaking changes.'
        ]
      }
    ]
  },
  {
    version: 'V1.0.2',
    date: 'Jan 26, 2026',
    sections: [
      {
        type: 'New',
        title: 'AI Assistant & Templates',
        items: [
          'AI Assistant in the left panel: natural-language search (e.g. "leads this week", "last 7 days") to get matching leads with optional Post Sales badge; View opens the lead in Pre Sales.',
          'Templates page: accordion view — show category only, expand to see subject and body; available in sidebar for admin and Surabhi.',
          'Extension Settings: options page to set Sales Hub URL so users can point to the deployed dashboard without editing code; popup has a Settings link.'
        ]
      },
      {
        type: 'Update',
        title: 'Email & Templates',
        items: [
          'CC is optional per recipient via "Include CC" checkbox; only checked recipients are included as CC.',
          'BCC is always Sales@amgsol.com (single line under toolbar; no UI to disable).',
          'Cmd+S / Ctrl+S saves on Templates page and in the Lead modal (no extra hint line).',
          'Email templates are loaded from the API; subject and body per stage come from stored templates.',
          'Post Sales: improved error message shows API URL and handles both { data: [] } and raw array responses for leads and post-sales data.'
        ]
      },
      {
        type: 'Bugfix',
        title: 'Email & Post Sales',
        items: [
          'Fixed CC list so unchecked recipients are not included in the email.',
          'Replaced invalid Handshake icon with HeartHandshake in Templates (lucide-react).',
          'Fixed ESLint no-undef in Post Sales by importing getDashboardApiUrl for the error message.'
        ]
      }
    ]
  },
  {
    version: 'V1.0.1',
    date: 'Jan 26, 2026',
    sections: [
      {
        type: 'New',
        title: 'Change Logs page',
        items: [
          'Added Change Logs section in the sidebar to view release notes.',
          'Display of New, Updates, and Bugfix categories with date-wise versions.'
        ]
      },
      {
        type: 'Update',
        title: 'AGS Sales Hub extension',
        items: [
          'Extension now supports LinkedIn Showcase pages in addition to Company pages.',
          'Popup capture prefers the active LinkedIn company or showcase tab for more reliable capture.'
        ]
      },
      {
        type: 'Bugfix',
        title: 'Extension capture on Showcase pages',
        items: [
          'Fixed capture not working when viewing LinkedIn Showcase pages (e.g. /showcase/...).',
          'Content script and popup now recognize both /company/ and /showcase/ URLs.'
        ]
      }
    ]
  },
  {
    version: 'V1.0.0',
    date: 'Dec 29, 2025',
    sections: [
      {
        type: 'New',
        title: 'Sales Hub launch',
        items: [
          'Pre Sales, Post Sales, Potential Clients, and Onboarded Clients modules.',
          'Follow-ups tracker and Send Emails integration.',
          'Analytics, Activity, and Highlights dashboards.',
          'AGS Sales Hub Chrome extension for capturing LinkedIn company info.'
        ]
      },
      {
        type: 'Update',
        title: 'Leads and filters',
        items: [
          'Date range and BDE filters across tabs.',
          'Export to Excel for Pre Sales, Post Sales, Potential Clients, and Onboarded Clients.'
        ]
      },
      {
        type: 'Bugfix',
        title: 'Initial release',
        items: [
          'Stability fixes for lead modal and follow-up completion flow.'
        ]
      }
    ]
  }
];

const TAG_STYLES = {
  New: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  Update: 'bg-blue-500/15 text-blue-700 border-blue-500/30',
  Bugfix: 'bg-amber-500/15 text-amber-700 border-amber-500/30'
};

const ChangeLogs = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Change Logs</h1>

      {CHANGE_LOG_DATA.map((release) => (
        <div key={release.version + release.date} className="mb-10">
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-lg font-semibold text-gray-800">{release.version}</span>
            <span className="text-sm text-gray-500">{release.date}</span>
          </div>

          <div className="space-y-6">
            {release.sections.map((section, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${TAG_STYLES[section.type] || 'bg-gray-100 text-gray-700'}`}
                >
                  {section.type}
                </span>
                <h3 className="mt-3 font-semibold text-gray-900">{section.title}</h3>
                <ul className="mt-2 list-disc list-inside space-y-1 text-gray-700 text-sm">
                  {section.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChangeLogs;
